import React from "react";
import { useNavigate } from "react-router-dom";
import { useRegistration } from "../../contexts/RegistrationContext";
import PrivateDetails from "./stages/PrivateDetails";
import Stages from "../Stages/Stages";
import LoginDetails from "./stages/loginDetails";
import BusinessDetails from "./stages/BusinessDetails";
import PackageDetails from "./stages/PackageDetails";
import BillingActivationStage from "./stages/BillingActivationStage";
import TrialActivationStage from "./stages/TrialActivationStage";
import { Loader } from "../Loader/Loader";
import {
  postRegistrationDraft,
  postAbandonRegistrationDraft,
  postBillingSession,
  postTrialRegistration,
  AGENT_REG_DRAFT_STORAGE_KEY,
  saveRegistrationFormSnapshot,
} from "../../services/agentRegisterBillingApi";
import { resolveBillingPackageIdAsync } from "../../utils/agentRegistrationPackageId";
import { buildRegistrationReferrerFields, isReferrerCodeInputValid } from "../../utils/agentReferralInvite";
import {
  buildTrialRegistrationPayload,
  isNoCardTrialPackage,
} from "../../utils/agentTrialRegistration";
import SuccessRegistrationPage from "./successRegistration/SuccessRegistrationPage";
import SuccessHeader from "./successRegistration/SuccessHeader";
import SuccessStatus from "./successRegistration/SuccessStatus";
import SuccessActions from "./successRegistration/SuccessActions";
import { logAgentRegBilling } from "../../utils/agentRegBillingLog";
import {
  getAgentRegistrationTestFormPatch,
  isAgentRegistrationTestFillAvailable,
} from "../../utils/agentRegistrationDevFill";
import { FormFields } from "../../contexts/RegistrationContext";
import { bigErrorMessage } from "../../services/swalService";
import {
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Modal } from "antd";
import AgentRegisterShell from "./agentRegister/AgentRegisterShell";
import AgentRegisterFormHeader from "./agentRegister/AgentRegisterFormHeader";

const Registration = () => {
  useTranslation();
  const navigate = useNavigate();
  const {
    getMissingFields,
    requiredFields,
    loading,
    formData,
    setFormData,
    referrerMatchOptions,
  } = useRegistration();
  const [currentStageIndex, setCurrentStageIndex] = React.useState(0);
  const [missingFields, setMissingFields] = React.useState([]);
  const [buttonLoading, setButtonLoading] = React.useState(false);
  const [billingDraftId, setBillingDraftId] = React.useState(null);
  const [draftConflict, setDraftConflict] = React.useState(null);
  const [trialSuccess, setTrialSuccess] = React.useState(false);
  const selectedPackage = formData[FormFields.ADDITIONAL_INFO.PACKAGE];
  const isNoCardTrial = isNoCardTrialPackage(selectedPackage);

  const stages = [
    {
      title: "פרטים אישיים",
      component: <PrivateDetails missingFields={missingFields} />,
      neededFields: requiredFields.PrivateDetails,
    },
    {
      title: "פרטי התחברות",
      component: <LoginDetails missingFields={missingFields} />,
      neededFields: requiredFields.LoginDetails,
    },
    {
      title: "קצת על העסק",
      component: <BusinessDetails missingFields={missingFields} />,
      neededFields: requiredFields.BusinessDetails,
    },
    {
      title: "בחירת חבילה",
      component: <PackageDetails missingFields={missingFields} />,
      neededFields: requiredFields.PackageDetails,
    },
    {
      title: isNoCardTrial ? "ניסיון ללא כרטיס" : "אמצעי תשלום",
      component: isNoCardTrial ? <TrialActivationStage /> : <BillingActivationStage />,
      neededFields: [],
    },
  ];

  const isPackageStage = currentStageIndex === 3;
  const isBillingStage = currentStageIndex === 4 && !isNoCardTrial;
  const isTrialStage = currentStageIndex === 4 && isNoCardTrial;
  const totalStepStages = stages.length;
  const stageSurfaceKey =
    currentStageIndex === 0
      ? "personal"
      : currentStageIndex === 1
        ? "login"
        : currentStageIndex === 2
          ? "business"
          : currentStageIndex === 3
            ? "package"
            : isNoCardTrial
              ? "trial"
              : "billing";

  React.useEffect(() => {
    if (currentStageIndex < 4) {
      setBillingDraftId(null);
      sessionStorage.removeItem(AGENT_REG_DRAFT_STORAGE_KEY);
    }
  }, [currentStageIndex]);

  function _setCurrentStageIndex(index) {
    if (index >= 0 && index < stages.length) {
      setCurrentStageIndex(index);
    }
  }

  function buildSafeRegistrationSnapshot() {
    const skip = new Set([
      FormFields.ACCOUNT_INFO.PASSWORD,
      FormFields.ACCOUNT_INFO.PASSWORD_AGAIN,
    ]);
    const o = {};
    Object.keys(formData).forEach((k) => {
      if (skip.has(k)) return;
      o[k] = formData[k];
    });
    return o;
  }

  /**
   * @param {{ reuseDraftId?: string, forceNewDraft?: boolean }} [opts]
   */
  async function handleContinueToHypBilling(opts = {}) {
    if (isNoCardTrialPackage(selectedPackage)) return;
    if (
      !isReferrerCodeInputValid(
        formData[FormFields.ADDITIONAL_INFO.RECOMMENDED_BY],
        referrerMatchOptions,
      )
    ) {
      setMissingFields([FormFields.ADDITIONAL_INFO.RECOMMENDED_BY]);
      setTimeout(() => setMissingFields([]), 5000);
      return;
    }
    const { reuseDraftId = null, forceNewDraft = false } = opts;
    setButtonLoading(true);
    try {
      let draftId = forceNewDraft ? null : reuseDraftId || billingDraftId;
      if (!draftId) {
        const { packageId, source: packageIdSource } = await resolveBillingPackageIdAsync(
          formData[FormFields.ADDITIONAL_INFO.PACKAGE],
        );
        const firstName = (formData[FormFields.PERSONAL_INFO.FIRST_NAME] || "").trim();
        const lastName = (formData[FormFields.PERSONAL_INFO.LAST_NAME] || "").trim();
        const fullName = `${firstName} ${lastName}`.trim();
        logAgentRegBilling("draft_create_request", { packageIdSource });
        const referrer = buildRegistrationReferrerFields(
          formData[FormFields.ADDITIONAL_INFO.RECOMMENDED_BY],
          referrerMatchOptions,
        );
        const draftRes = await postRegistrationDraft({
          fullName,
          firstName,
          lastName,
          idNumber: (formData[FormFields.PERSONAL_INFO.NATIONAL_ID] || "").trim(),
          email: formData[FormFields.PERSONAL_INFO.EMAIL],
          phone: formData[FormFields.PERSONAL_INFO.CELL],
          password: formData[FormFields.ACCOUNT_INFO.PASSWORD],
          businessName: formData[FormFields.COMPANY_INFO.COMPANY_NAME],
          packageId,
          packageCode: selectedPackage,
          ...referrer,
        });
        draftId = draftRes.draftId;
        logAgentRegBilling("draft_created", { draftId });
        setBillingDraftId(draftId);
        sessionStorage.setItem(AGENT_REG_DRAFT_STORAGE_KEY, draftId);
      } else {
        setBillingDraftId(draftId);
        sessionStorage.setItem(AGENT_REG_DRAFT_STORAGE_KEY, draftId);
        logAgentRegBilling(reuseDraftId ? "draft_resume_reuse" : "draft_reuse_existing_state", {
          draftId,
        });
      }
      logAgentRegBilling("billing_session_request", { draftId });
      const sessionRes = await postBillingSession({ draftId });
      logAgentRegBilling("billing_session_ok", {
        draftId,
        expiresInSeconds: sessionRes.expiresInSeconds,
      });
      const url = sessionRes.paymentUrl;
      if (!url) {
        throw new Error("לא התקבל קישור תשלום מהשרת");
      }
      saveRegistrationFormSnapshot(buildSafeRegistrationSnapshot());
      logAgentRegBilling("redirecting_to_hyp", { draftId });
      window.location.assign(url);
    } catch (err) {
      if (err?.code === "REGISTRATION_CONFLICT" && err.conflictPayload) {
        setDraftConflict(err.conflictPayload);
        logAgentRegBilling("draft_conflict_ui", {
          draft_status: err.conflictPayload.draft_status,
          can_resume: err.conflictPayload.can_resume,
        });
        return;
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "שגיאה ביצירת דף התשלום";
      logAgentRegBilling("draft_or_billing_error", {
        status: err?.response?.status,
        err: String(msg).slice(0, 200),
      });
      bigErrorMessage(typeof msg === "string" ? msg : String(msg));
    } finally {
      setButtonLoading(false);
    }
  }

  async function handleRestartRegistrationDraft() {
    const id = draftConflict?.draft_id;
    if (!id) return;
    setDraftConflict(null);
    setButtonLoading(true);
    try {
      await postAbandonRegistrationDraft(id);
      sessionStorage.removeItem(AGENT_REG_DRAFT_STORAGE_KEY);
      setBillingDraftId(null);
      logAgentRegBilling("draft_restart_after_abandon", {});
      await handleContinueToHypBilling({ forceNewDraft: true });
    } catch (err) {
      if (err?.code === "REGISTRATION_CONFLICT" && err.conflictPayload) {
        setDraftConflict(err.conflictPayload);
        return;
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "לא ניתן להתחיל מחדש כרגע";
      bigErrorMessage(typeof msg === "string" ? msg : String(msg));
    } finally {
      setButtonLoading(false);
    }
  }

  const isRTL =
    typeof document !== "undefined" &&
    (document.dir === "rtl" || document.documentElement.dir === "rtl");
  const BackIcon = isRTL ? ArrowRightOutlined : ArrowLeftOutlined;
  const NextIcon = isRTL ? ArrowLeftOutlined : ArrowRightOutlined;

  const showBackButton = currentStageIndex !== 0;
  const primaryLabel = isTrialStage
    ? "סיום הרשמה ללא כרטיס"
    : isBillingStage
      ? "המשך לשמירת אמצעי תשלום"
      : "המשך";

  async function handleSubmitTrialRegistration() {
    if (
      !isReferrerCodeInputValid(
        formData[FormFields.ADDITIONAL_INFO.RECOMMENDED_BY],
        referrerMatchOptions,
      )
    ) {
      setMissingFields([FormFields.ADDITIONAL_INFO.RECOMMENDED_BY]);
      setTimeout(() => setMissingFields([]), 5000);
      return;
    }
    setButtonLoading(true);
    try {
      const { packageId } = await resolveBillingPackageIdAsync(selectedPackage);
      const body = buildTrialRegistrationPayload(formData, {
        packageId,
        referrerOptions: referrerMatchOptions,
      });
      logAgentRegBilling("trial_registration_request", {
        packageCode: body.packageCode,
        trialDays: body.trialDays,
        hasReferrer: Boolean(body.referrerAgentId),
      });
      await postTrialRegistration(body);
      logAgentRegBilling("trial_registration_ok", { packageCode: body.packageCode });
      setTrialSuccess(true);
    } catch (err) {
      if (err?.code === "REGISTRATION_CONFLICT") {
        bigErrorMessage(
          err.conflictPayload?.message ||
            "כבר קיימת הרשמה לכתובת האימייל הזו. אין צורך בכרטיס אשראי לניסיון.",
        );
        return;
      }
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "לא ניתן להשלים את הרשמת הניסיון כרגע";
      bigErrorMessage(typeof msg === "string" ? msg : String(msg));
    } finally {
      setButtonLoading(false);
    }
  }

  const renderPrimaryAction = (fullWidth = false) => {
    if (buttonLoading) {
      return (
        <button
          type="button"
          className="agent-register-btn agent-register-btn--primary"
          disabled
          style={fullWidth ? { width: "100%" } : undefined}
        >
          שולח…
        </button>
      );
    }
    return (
      <button
        type="button"
        className="agent-register-btn agent-register-btn--primary"
        style={fullWidth ? { width: "100%" } : undefined}
        onClick={() => {
          if (isTrialStage) {
            handleSubmitTrialRegistration();
            return;
          }
          if (isBillingStage) {
            handleContinueToHypBilling();
            return;
          }
          const neededForStep = stages[currentStageIndex].neededFields;
          const tempMissingFields = getMissingFields().filter((key) =>
            neededForStep.includes(key),
          );
          if (
            isPackageStage &&
            !isReferrerCodeInputValid(
              formData[FormFields.ADDITIONAL_INFO.RECOMMENDED_BY],
              referrerMatchOptions,
            )
          ) {
            tempMissingFields.push(FormFields.ADDITIONAL_INFO.RECOMMENDED_BY);
          }
          if (tempMissingFields.length > 0) {
            setMissingFields(tempMissingFields);
            setTimeout(() => setMissingFields([]), 5000);
            return;
          }
          setCurrentStageIndex(currentStageIndex + 1);
        }}
      >
        {isBillingStage || isTrialStage ? <CheckOutlined /> : <NextIcon />}
        {primaryLabel}
      </button>
    );
  };

  const renderSecondaryAction = () => {
    if (buttonLoading) return null;
    if (!showBackButton) return null;
    return (
      <button
        type="button"
        className="agent-register-btn agent-register-btn--ghost"
        onClick={() => setCurrentStageIndex(currentStageIndex - 1)}
      >
        <BackIcon />
        חזור
      </button>
    );
  };

  const slideBodyRefs = React.useRef([]);

  React.useEffect(() => {
    const active = slideBodyRefs.current[currentStageIndex];
    if (active && typeof active.scrollTo === "function") {
      active.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [currentStageIndex]);

  if (trialSuccess) {
    return (
      <SuccessRegistrationPage>
        <SuccessHeader
          title="נרשמתם לניסיון של 60 יום"
          subtitle="החשבון נפתח בלי כרטיס אשראי ובלי חיוב"
        />
        <SuccessStatus
          items={["לא נדרש כרטיס אשראי", "60 יום ניסיון ללא עלות", "אחרי הניסיון בוחרים חבילה בתשלום"]}
        />
        <SuccessActions
          onLogin={() => navigate("/login")}
          onDashboard={() => navigate("/login")}
          onStartSelling={() => navigate("/login")}
        />
      </SuccessRegistrationPage>
    );
  }

  return (
    <AgentRegisterShell variant="full">
      <Modal
        title="הרשמה קיימת לכתובת האימייל"
        open={Boolean(draftConflict)}
        onCancel={() => setDraftConflict(null)}
        footer={null}
        destroyOnHidden
        maskClosable={false}
      >
        <p style={{ marginBottom: 16 }}>
          {draftConflict?.message ||
            "נמצאה הרשמה שלא הושלמה. אפשר להמשיך לשמירת אמצעי תשלום או לבטל את הטיוטה ולהתחיל מחדש."}
        </p>
        {draftConflict?.can_resume === false ? (
          <p className="agent-register-microcopy" style={{ marginBottom: 12 }}>
            הטיוטה בשלב מתקדם אחרי תשלום. אם ההרשמה נתקעה, השתמשו ב&quot;התחל מחדש&quot; כדי לפתוח טיוטה
            חדשה (רק אם אין חשבון סוכן פעיל באותו אימייל).
          </p>
        ) : null}
        <div className="agent-register-actions" style={{ marginTop: 8 }}>
          {draftConflict?.can_resume !== false ? (
            <button
              type="button"
              className="agent-register-btn agent-register-btn--primary"
              disabled={buttonLoading}
              onClick={() => {
                const id = draftConflict?.draft_id;
                if (!id) return;
                setDraftConflict(null);
                handleContinueToHypBilling({ reuseDraftId: id });
              }}
            >
              המשך הרשמה
            </button>
          ) : null}
          <button
            type="button"
            className="agent-register-btn agent-register-btn--ghost"
            disabled={buttonLoading}
            onClick={() => handleRestartRegistrationDraft()}
          >
            התחל מחדש
          </button>
          <button
            type="button"
            className="agent-register-btn agent-register-btn--ghost"
            disabled={buttonLoading}
            onClick={() => setDraftConflict(null)}
          >
            ביטול
          </button>
        </div>
      </Modal>
      <div className="agent-register-form-inner" data-stage={stageSurfaceKey}>
        {loading ? (
          <div className="agent-register-loading">
            <Loader />
          </div>
        ) : (
          <>
            <div className="agent-register-form-top">
              <AgentRegisterFormHeader
                stageTitle={stages[currentStageIndex]?.title}
                stepIndex={currentStageIndex}
                totalSteps={totalStepStages}
              />

              {isAgentRegistrationTestFillAvailable() ? (
                <div className="agent-register-dev-fill">
                  <button
                    type="button"
                    className="agent-register-btn agent-register-btn--ghost"
                    onClick={() => {
                      setFormData((prev) => ({
                        ...prev,
                        ...getAgentRegistrationTestFormPatch(),
                      }));
                      setMissingFields([]);
                    }}
                  >
                    מלא נתוני בדיקה
                  </button>
                </div>
              ) : null}

              <Stages
                className="agent-register-steps"
                stages={stages}
                currentIndex={currentStageIndex}
                setCurrentStageIndex={_setCurrentStageIndex}
                missingFields={missingFields}
              />
            </div>

            <div className="agent-register-compose">
              <div
                className="agent-register-slider"
                data-slide-dir={isRTL ? "rtl" : "ltr"}
                aria-live="polite"
                aria-atomic="false"
              >
                <div className="agent-register-slider__track">
                  {stages.map((stage, index) => {
                    const isActive = index === currentStageIndex;
                    // LTR: next sits to the right (+). RTL: next sits to the left (−)
                    // so advancing moves content toward inline-start.
                    const delta = index - currentStageIndex;
                    const offsetPercent = isRTL ? delta * -100 : delta * 100;
                    const isNear = Math.abs(delta) <= 1;
                    return (
                      <div
                        key={`agent-register-slide-${index}`}
                        className={`agent-register-slider__slide${isActive ? " is-active" : ""}${isNear ? " is-near" : ""}`}
                        style={{ transform: `translate3d(${offsetPercent}%, 0, 0)` }}
                        aria-hidden={!isActive}
                        {...(!isActive ? { inert: true } : {})}
                      >
                        <div
                          ref={(el) => {
                            slideBodyRefs.current[index] = el;
                          }}
                          className="agent-register-slider__body"
                        >
                          {stage.component}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="agent-register-form-footer">
                <div className="agent-register-actions">
                  {renderSecondaryAction()}
                  {renderPrimaryAction(false)}
                </div>
                {!isBillingStage && !isTrialStage ? (
                  <>
                    <p className="agent-register-microcopy">
                      ללא התחייבות · לאחר האישור נציג יצור קשר להשלמת ההקמה · בדרך כלל פתיחה תוך 24–72 שעות
                    </p>
                    <div className="agent-register-trust-row">
                      <span>הנתונים נשלחים בחיבור מאובטח</span>
                      <span>אין חיוב בשלב הרישום</span>
                      <span>ניסיון 60 יום ללא כרטיס</span>
                    </div>
                  </>
                ) : isTrialStage ? (
                  <p className="agent-register-microcopy" style={{ marginTop: 8 }}>
                    ההרשמה מסתיימת כאן. לא עוברים לדף תשלום ולא שומרים כרטיס אשראי.
                  </p>
                ) : (
                  <p className="agent-register-microcopy" style={{ marginTop: 8 }}>
                    תועברו לדף תשלום חיצוני (Hyp). לא מזינים כרטיס באתר HotelsRiver.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AgentRegisterShell>
  );
};

export default Registration;
